import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import omit from 'lodash/omit';
import set from 'lodash/set';
import times from 'lodash/times';
import { retry } from '@eventbrite/retry';
import logger from '@eventbrite/client-logger';
import { getOrderFlowHeaders } from '../base';
import { getTokens } from '../../actions/tokens';
import {
    formatTicketsForOrderSummary,
    isDonationTicket,
    isAddonTicket,
} from '../../utils/tickets';
import {
    getSurveyAnswers,
    transformAddressForApi,
    removeCopyBillingInfoField,
} from '../../utils/surveys';
import { isShippingInDeliveryMethodsPayload } from '../../utils/deliveryMethod';
import { FULFILLMENT_ADDRESS } from '../../constants';
import {
    _getAbandonOrderUrl,
    _getAvailablePaymentMethodsUrl,
    _getCreateOrderUrl,
    _getOrderUrl,
    _getOrderAttendeesUrl,
    _getOrderEligibilityUrl,
    _getPlaceOrderUrl,
    _getResendEmailUrl,
    _getUpdateOrderUrl,
    getPaymentHistoryUrl,
    getLastPaymentStatusUrl,
    _updateEmailPreferencesUrl,
} from '../constants';
import { INTERNAL_SERVER_ERROR } from '../../containers/checkout/constants';
import {
    _fetchV3WithOrderErrorHandling,
    _fetchV3WithOrderSurveyErrorHandling,
} from './errors';

export {
    clearPaymentMethods,
    formatCreditCardInstrument,
    isAllInfoRequiredToSavePaymentMethodAvailable,
    savePaymentMethods,
} from './paymentMethods';

export { getOrderStatus } from './orderStatus';

/**
 * Return a `fetch` Promise to create the order
 *
 * @param {object} order info object that has the eventId and a map of the selectedTickets
 */
export const createOrder = ({
    affiliateCode,
    eventId,
    inviteToken,
    campaignToken,
    promoCode,
    selectedTickets,
    teamToken,
    waitlistCode,
}) => {
    const tickets = [];

    formatTicketsForOrderSummary(selectedTickets).forEach((selectedTicket) => {
        const { selectedQuantity, id, isVariant, cost } = selectedTicket;
        let ticket = {
            ticket_class_id: id,
        };

        if (isVariant) {
            ticket = {
                variant_id: id,
            };
        }

        if (isDonationTicket(selectedTicket) && cost) {
            ticket['donation_amount'] = {
                currency: cost.currency,
                value: cost.value,
            };
        }

        if (teamToken && !isAddonTicket(selectedTicket)) {
            ticket['team_token'] = teamToken;
        }

        times(selectedQuantity, () => tickets.push(ticket));
    });

    const postParams = {
        method: 'POST',
        headers: getOrderFlowHeaders(getTokens()),

        body: JSON.stringify({
            tickets,
            affiliate_code: affiliateCode,
            application: 'embedded_web',
            event_id: eventId,
            promo_code: promoCode,
            recipient_invite_token: inviteToken,
            team_token: teamToken,
            waitlist_code: waitlistCode,
            campaign_token: campaignToken,
        }),
    };

    return _fetchV3WithOrderErrorHandling(_getCreateOrderUrl(), postParams);
};

/**
 * Returns a `fetch` Promise to get the order's attendees
 * We need to call the endpoint and cycle through the pages
 * until we've received all attendees in the order.
 *
 * @param {string} orderId
 * @param {string=} expansion provide this param if want to customize the `?expand` expansion
 */
export const getOrderAttendees = async (orderId, expansion = 'survey') => {
    let allAttendees = [];
    let page = 1;
    let hasMoreItems;

    do {
        const payload = await _fetchV3WithOrderErrorHandling(
            _getOrderAttendeesUrl(orderId, { expansion, page }),
            {
                headers: getOrderFlowHeaders(getTokens()),
            },
        );

        hasMoreItems = get(
            payload,
            'response.pagination.has_more_items',
            false,
        );
        allAttendees = [...allAttendees, ...payload.response.attendees];
        page++;
    } while (hasMoreItems);

    return allAttendees;
};

/**
 * Return a `fetch` Promise to get the available payment methods for a certain order
 *
 * @param {string} eventId The eventId of the event.
 * @param {string} orderId The orderId of the order.
 */
export const getAvailablePaymentMethods = (eventId, orderId) =>
    _fetchV3WithOrderErrorHandling(
        _getAvailablePaymentMethodsUrl(eventId, orderId),
        {
            headers: getOrderFlowHeaders(getTokens()),
        },
    );

/**
 * Return a `fetch` Promise to update the order
 *
 * @param {object} param
 * @param {string} param.orderId              order id to be updated
 * @param {object} param.formData             form data provided by checkout form
 * @param {object} param.deliveryMethods      delivery methods to save, if any
 */
export const saveOrder = ({ orderId, formData, deliveryMethods = null }) => {
    const formDataSurveyAnswers = omit(
        formData,
        'paymentMethod',
        'payment',
        'bancontact',
        'vaultId',
        'userInstrument',
        `buyer.${FULFILLMENT_ADDRESS}`,
    );
    const surveyAnswers = getSurveyAnswers(
        removeCopyBillingInfoField(formDataSurveyAnswers),
    );

    const orderInfo = {
        order: {
            answers: surveyAnswers,
        },
    };

    if (deliveryMethods) {
        set(orderInfo, 'order.delivery_methods', deliveryMethods);
    }

    const fulfillmentAddress =
        isShippingInDeliveryMethodsPayload(deliveryMethods) && formData.buyer
            ? formData.buyer[FULFILLMENT_ADDRESS]
            : {};

    if (!isEmpty(fulfillmentAddress)) {
        set(
            orderInfo,
            'order.fulfillment_address',
            transformAddressForApi(fulfillmentAddress),
        );
    }

    const body = JSON.stringify(orderInfo);

    return _fetchV3WithOrderErrorHandling(_getUpdateOrderUrl(orderId), {
        method: 'POST',
        headers: getOrderFlowHeaders(getTokens()),
        body,
    });
};

/**
 * Return a `fetch` Promise to place the order
 *
 * @param {string} orderId order id to be placed
 */
export const placeOrder = (orderId, body = {}) =>
    _fetchV3WithOrderErrorHandling(_getPlaceOrderUrl(orderId), {
        headers: getOrderFlowHeaders(getTokens()),
        method: 'POST',
        body: JSON.stringify(body),
    });

/**
 * Return a `fetch` Promise to place the order
 *
 * @note If server returns an unknown error 500 the request will be retried up to 5 times.
 *
 * @param {string} orderId order id to be placed
 */
// TODO: EB-130249 Remove this function once race condition is fixed
export const placeOrderRetrying = async (
    orderId,
    body = {},
    loggerAdditionalInfo = {},
) => {
    const isErrorRetryable = (error) =>
        error.extraData.response.status === 500 &&
        error.extraData.parsedError.error === INTERNAL_SERVER_ERROR;

    const setLoggerGroupingHash = (report) => {
        // eslint-disable-next-line no-param-reassign
        report.groupingHash = 'response-status-in-place-order';
    };

    const loggerOptions = {
        beforeSend: setLoggerGroupingHash,
    };

    /**
     * @param {number} timesAttempted
     */
    const attemptPlaceOrder = async (timesAttempted) => {
        const result = await placeOrder(orderId, body);

        if (timesAttempted > 0) {
            logger.info(
                `placeOrder retried and succeeded after ${timesAttempted} attempts`,
                {
                    ...loggerAdditionalInfo,
                    timesAttempted,
                    orderId,
                },
                loggerOptions,
            );
        }

        return result;
    };

    /**
     * @param {Error} error
     * @param {number} timesAttempted
     */
    const onFailedLastAttempt = (error, timesAttempted) => {
        const errorMessage = isErrorRetryable(error)
            ? `placeOrder failed after ${timesAttempted} attempts`
            : `placeOrder failed after ${timesAttempted} attempts due to an error that normally would not be retried`;

        logger.error(
            errorMessage,
            {
                ...loggerAdditionalInfo,
                timesAttempted,
                orderId,
                isErrorRetryable: isErrorRetryable(error),
            },
            loggerOptions,
        );
    };

    return retry(attemptPlaceOrder, {
        maxAttempts: 5,
        minDelayInMilliseconds: 2500,
        shouldRetry: isErrorRetryable,
        onFailedLastAttempt,
    });
};

const _handleResponse = (response) => {
    const responseHeaders = response.headers;

    return response.json().then((responseData) =>
        Promise.resolve({
            response: responseData,
            headers: responseHeaders,
        }),
    );
};

/**
 * Return a `fetch` Promise to update the order
 *
 * @param {string} orderId order id to be updated
 * @param {object} stateTokens tokens from state used to authorize order_service calls
 * @param {object} options options, can be used to pass expansions
 */
export const updateOrder = (orderId, stateTokens, options) => {
    // Prevent this ajax call from getting cached by adding a cache buster to the url
    const currentTime = new Date().getTime();
    const url = `${_getUpdateOrderUrl(orderId, options)}${
        options ? '&' : '?'
    }cb=${currentTime}`;

    //TODO: EB-130741 we should not have to rely on locally saved tokens (getTokens())
    // when we have them on the state
    const tokens = stateTokens || getTokens();

    return _fetchV3WithOrderErrorHandling(url, {
        headers: getOrderFlowHeaders(tokens),
        method: 'GET',
    });
};

/**
 * Return a `fetch` Promise to retrieve payment history for an order.
 *
 * @param  {object} orderId order id to check payment history for
 */
export const getPaymentHistory = (orderId) => {
    // Prevent this ajax call from getting cached by adding a cache buster to the url
    const currentTime = new Date().getTime();
    const paymentHistoryEndpoint = getPaymentHistoryUrl(currentTime);

    const url = `${paymentHistoryEndpoint}&reference_id=${orderId}`;

    return () => fetch(url).then(_handleResponse);
};

/**
 * Return a `fetch` Promise to retrieve the last payment status for an order.
 *
 * @param  {object} orderId order id to check payment history for
 */
export const getLastPaymentStatus = (orderId) => {
    // Prevent this ajax call from getting cached by adding a cache buster to the url
    const currentTime = new Date().getTime();

    const urlLastPaymentStatus = `${getLastPaymentStatusUrl(
        currentTime,
    )}&reference_id=${orderId}`;

    return () =>
        fetch(urlLastPaymentStatus).then((response) => {
            return _handleResponse(response);
        });
};

export const getAdyenBanks = (url) => {
    const fullUrl = `https://cors-anywhere.herokuapp.com/${url}`;

    return () => fetch(fullUrl).then(_handleResponse);
};

/**
 * Return a `fetch` Promise to resend the order confirmation email
 *
 * @param {string} orderId order id to be placed
 * @param {string} recipientEmail recipient email
 */
export const resendEmail = (orderId, recipientEmail) =>
    _fetchV3WithOrderErrorHandling(_getResendEmailUrl(orderId), {
        method: 'POST',
        headers: getOrderFlowHeaders(getTokens()),

        body: JSON.stringify({
            recipient_email: recipientEmail,
        }),
    });

/**
 * Return a `fetch` Promise to abandon the order
 *
 * @param {string} orderId order id to be abandoned
 */
export const abandonOrder = (orderId) =>
    _fetchV3WithOrderErrorHandling(_getAbandonOrderUrl(orderId), {
        method: 'POST',
        headers: getOrderFlowHeaders(getTokens()),
    });

/**
 * Return a `fetch` Promise to update the order owner's email preferences
 *
 * @param {string} orderId The order for which we want to update the owner's email address.
 * @param {boolean} ebMarketingOptIn Whether or not the user wants marketing emails from eb.
 */
export const updateEmailPreferences = (orderId, ebMarketingOptIn) =>
    _fetchV3WithOrderErrorHandling(_updateEmailPreferencesUrl(orderId), {
        method: 'POST',
        headers: getOrderFlowHeaders(getTokens()),
        body: JSON.stringify({
            eb_marketing_opt_in: ebMarketingOptIn,
        }),
    }).catch((error) => {
        // 403 FORBIDDEN occurs when the order owner is different from the logged in user.
        // This happens when the user is already logged into eb.com under one email,
        // but uses another email in the buyer info fields.
        //
        // We are silencing this error because we are choosing to just not update
        // the user's email preferences in this case.
        if (error.extraData.response.status !== 403) {
            throw error;
        }
    });

/**
 * Return a `fetch` Promise to retrieve the order
 *
 * @param {string} orderId order id to be retrieved
 */
export const getOrder = (orderId) =>
    _fetchV3WithOrderErrorHandling(_getOrderUrl(orderId), {
        method: 'GET',
        headers: getOrderFlowHeaders(getTokens()),
    });

/**
 * Check whether the order is eligible to be placed.
 * Checkout Order Eligibility endpoint
 * https://eventbriteapiv3.docs.apiary.io/#reference/order/order-operations/check-order-eligibility
 */
export const getOrderEligibility = (orderId) =>
    _fetchV3WithOrderErrorHandling(_getOrderEligibilityUrl(orderId), {
        method: 'GET',
        headers: getOrderFlowHeaders(getTokens()),
    });

/**
 * Return a `fetch` Promise to update the order survey questions
 *
 * @param {string} orderId          ID of the order to be updated
 * @param {object} surveyAnswers    Survey answers
 */
export const updateOrderSurveyAnswersAfterPlacement = (
    orderId,
    surveyAnswers,
) => {
    const orderInfo = {
        order: {
            answers: surveyAnswers,
        },
    };
    const body = JSON.stringify(orderInfo);

    return _fetchV3WithOrderSurveyErrorHandling(_getUpdateOrderUrl(orderId), {
        method: 'POST',
        headers: getOrderFlowHeaders(getTokens()),
        body,
    });
};
